import React, { useState } from 'react';
import { useAppState } from '../../context/AppStateProvider';
import { useAuth } from '../../context/AuthContext';
import { Wrench, Plus, Folder, ExternalLink, Trash2, GripVertical } from 'lucide-react';
import SearchBar from '../shared/SearchBar';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableItem = ({ item, onDelete, setSelectedItem, onToolClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="item-card-sortable" data-testid={`tool-item-${item.id}`}>
      <div className="drag-handle" {...attributes} {...listeners}>
        <GripVertical size={16} />
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="item-link"
        onClick={(e) => {
          setSelectedItem(item);
          if (onToolClick) {
            onToolClick(e, item.id, item.url);
          }
        }}
      >
        <div className="item-header">
          <span className="item-title">{item.title}</span>
          <ExternalLink size={14} className="external-icon" />
        </div>
        <div className="item-url">{new URL(item.url).hostname}</div>
      </a>
      <button
        className="item-delete-btn"
        data-testid={`delete-item-${item.id}`}
        onClick={(e) => {
          e.preventDefault();
          onDelete(item.id);
        }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

const WorkspaceTools = () => {
  const { folders, items, addFolder, deleteFolder, deleteItem, setSelectedItem, searchQuery, refreshItems } = useAppState();
  const { token } = useAuth();
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [sortedItems, setSortedItems] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const toolFolders = folders.filter(f => f.category === 'tools');
  const toolItems = items.filter(i => i.category === 'tools');

  const filteredItems = searchQuery 
    ? toolItems.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.url.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : toolItems;

  // Group items and sort alphabetically
  const ungroupedItems = filteredItems
    .filter(i => !i.folderId)
    .sort((a, b) => a.title.localeCompare(b.title));

  React.useEffect(() => {
    setSortedItems(ungroupedItems);
  }, [ungroupedItems.length]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleToolClick = async (e, toolId, toolUrl) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/items/${toolId}/track-click`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshItems();
      window.open(toolUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to track click:', error);
      window.open(toolUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSortedItems((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddFolder = () => {
    if (newFolderName.trim()) {
      addFolder(newFolderName.trim(), 'tools');
      setNewFolderName('');
      setShowAddFolder(false);
    }
  };

  return (
    <div className="workspace-container" data-testid="workspace-tools">
      <div className="workspace-header">
        <div className="workspace-header-left">
          <Wrench className="workspace-icon" size={24} />
          <div>
            <h2 className="workspace-title">Tools</h2>
            <p className="workspace-subtitle">{filteredItems.length} items</p>
          </div>
        </div>
        <button 
          className="add-folder-btn"
          data-testid="add-folder-btn"
          onClick={() => setShowAddFolder(!showAddFolder)}
        >
          <Plus size={18} />
          Add Folder
        </button>
      </div>

      <SearchBar placeholder="Search tools..." />

      {showAddFolder && (
        <div className="add-folder-form" data-testid="add-folder-form">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name"
            className="folder-input"
            data-testid="folder-name-input"
            onKeyPress={(e) => e.key === 'Enter' && handleAddFolder()}
          />
          <button onClick={handleAddFolder} className="save-btn" data-testid="save-folder-btn">Save</button>
          <button onClick={() => setShowAddFolder(false)} className="cancel-btn" data-testid="cancel-folder-btn">Cancel</button>
        </div>
      )}

      <div className="content-area">
        {toolFolders.length === 0 && filteredItems.filter(i => !i.folderId).length === 0 ? (
          <div className="empty-state-large" data-testid="empty-state">
            <Wrench size={48} className="empty-icon" />
            <p>No tools yet</p>
            <p className="empty-hint">Use Quick Capture to add your first tool</p>
          </div>
        ) : (
          <>
            {/* Ungrouped items */}
            {sortedItems.length > 0 && (
              <div className="items-section">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortedItems.map(i => i.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="items-grid-sortable">
                      {sortedItems.map(item => (
                        <SortableItem
                          key={item.id}
                          item={item}
                          onDelete={deleteItem}
                          setSelectedItem={setSelectedItem}
                          onToolClick={handleToolClick}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}

            {/* Folders */}
            {toolFolders.map(folder => {
              const folderItems = filteredItems
                .filter(i => i.folderId === folder.id)
                .sort((a, b) => a.title.localeCompare(b.title));
              if (searchQuery && folderItems.length === 0) return null;
              
              return (
                <div key={folder.id} className="folder-section" data-testid={`folder-${folder.id}`}>
                  <div className="folder-header">
                    <Folder size={18} className="folder-icon" />
                    <h3 className="folder-title">{folder.name}</h3>
                    <span className="folder-count">({folderItems.length})</span>
                    <button
                      className="folder-delete-btn"
                      data-testid={`delete-folder-${folder.id}`}
                      onClick={() => deleteFolder(folder.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={folderItems.map(i => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="items-grid-sortable">
                        {folderItems.map(item => (
                          <SortableItem
                            key={item.id}
                            item={item}
                            onDelete={deleteItem}
                            setSelectedItem={setSelectedItem}
                            onToolClick={handleToolClick}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default WorkspaceTools;


